import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  LocalOffer as PriceIcon,
  Inventory as StockIcon,
  Redeem as RedeemIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Prize, PrizeRedemption } from '../../types';
import { prizeService } from '../../services/prizeService';
import { useAuth } from '../../context/AuthContext';
import CreatePrizeDialog from './CreatePrizeDialog';
import IconButton from '@mui/material/IconButton';

interface IndividualPrizesTabProps {
  prizes: Prize[];
  teamId: string | null;
  onPrizeRedeemed: () => void;
  canManagePrizes?: boolean;
  coachId?: string;
  onPrizeUpdated?: () => void;
}

const IndividualPrizesTab: React.FC<IndividualPrizesTabProps> = ({
  prizes,
  teamId,
  onPrizeRedeemed,
  canManagePrizes = false,
  coachId = '',
  onPrizeUpdated
}) => {
  const { user } = useAuth();
  const [redemptionDialogOpen, setRedemptionDialogOpen] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null);
  const [redemptionNotes, setRedemptionNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editPrize, setEditPrize] = useState<Prize | null>(null);
  const [deleteConfirmPrize, setDeleteConfirmPrize] = useState<Prize | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleRedeemPrize = (prize: Prize) => {
    setSelectedPrize(prize);
    setRedemptionNotes('');
    setRedemptionDialogOpen(true);
  };

  const handleConfirmRedemption = async () => {
    if (!selectedPrize || !user || !teamId) return;

    setLoading(true);
    setError('');

    try {
      await prizeService.requestPrizeRedemption(
        selectedPrize.id,
        user.id,
        teamId,
        selectedPrize.pointCost
      );

      setRedemptionDialogOpen(false);
      setSelectedPrize(null);
      setSuccessMessage(`Successfully requested ${selectedPrize.name}! Your coach will review and approve your redemption.`);
      onPrizeRedeemed();
    } catch (err: any) {
      console.error('Error redeeming prize:', err);
      setError(err.message || 'Failed to redeem prize');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmPrize) return;
    setDeleting(true);
    try {
      await prizeService.deletePrize(deleteConfirmPrize.id);
      setDeleteConfirmPrize(null);
      setSuccessMessage(`Prize "${deleteConfirmPrize.name}" removed.`);
      onPrizeUpdated?.();
    } catch (err: any) {
      console.error('Error deleting prize:', err);
      setError(err.message || 'Failed to remove prize');
    } finally {
      setDeleting(false);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'physical': return 'primary';
      case 'digital': return 'secondary';
      case 'experience': return 'success';
      case 'custom': return 'warning';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'physical': return '🏆';
      case 'digital': return '💻';
      case 'experience': return '🎯';
      case 'custom': return '⭐';
      default: return '🎁';
    }
  };

  if (prizes.length === 0) {
    return (
      <Alert severity="info">
        No individual prizes available yet. Check back later or ask your coach to add some prizes!
      </Alert>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {prizes.map((prize) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={prize.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {prize.imageUrl && (
                <CardMedia
                  component="img"
                  height="200"
                  image={prize.imageUrl}
                  alt={prize.name}
                />
              )}
              
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" component="h3">
                    {prize.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {canManagePrizes && (
                      <>
                        <IconButton size="small" color="primary" onClick={() => setEditPrize(prize)} title="Edit prize">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => { setError(''); setDeleteConfirmPrize(prize); }} title="Remove prize">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                    <Chip
                      icon={<TrophyIcon />}
                      label={getCategoryIcon(prize.category)}
                      color={getCategoryColor(prize.category) as any}
                      size="small"
                    />
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                  {prize.description}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PriceIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    {prize.pointCost} points
                  </Typography>
                </Box>

                {prize.maxQuantity && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <StockIcon color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {prize.currentQuantity || 0} remaining
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained"
                  startIcon={<RedeemIcon />}
                  onClick={() => handleRedeemPrize(prize)}
                  disabled={prize.maxQuantity ? (prize.currentQuantity || 0) <= 0 : false}
                  fullWidth
                >
                  Redeem Prize
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Redemption Confirmation Dialog */}
      <Dialog
        open={redemptionDialogOpen}
        onClose={() => setRedemptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Redeem Prize: {selectedPrize?.name}
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to redeem this prize for {selectedPrize?.pointCost} points?
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedPrize?.description}
          </Typography>

          <TextField
            fullWidth
            label="Notes (optional)"
            multiline
            rows={3}
            value={redemptionNotes}
            onChange={(e) => setRedemptionNotes(e.target.value)}
            placeholder="Add any notes about your redemption request..."
            sx={{ mt: 2 }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setRedemptionDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRedemption}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <RedeemIcon />}
          >
            {loading ? 'Redeeming...' : 'Confirm Redemption'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirmPrize} onClose={() => !deleting && setDeleteConfirmPrize(null)}>
        <DialogTitle>Remove prize?</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography>
            Remove &quot;{deleteConfirmPrize?.name}&quot;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmPrize(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit prize dialog (coaches only) */}
      {canManagePrizes && teamId && coachId && (
        <CreatePrizeDialog
          open={!!editPrize}
          onClose={() => setEditPrize(null)}
          onPrizeCreated={() => { setEditPrize(null); onPrizeUpdated?.(); }}
          teamId={teamId}
          coachId={coachId}
          existingPrize={editPrize ?? undefined}
        />
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
    </Box>
  );
};

export default IndividualPrizesTab;
